import { FieldArray, Form, Formik } from "formik";
import { marked } from 'marked';
import React, { useRef } from "react";
import * as XLSX from "xlsx";
import { ModalType } from "../../../../config/ModalTypes";
import { useAiChat } from "../../../../hooks/useAiChat";
import useAiTools, { DownloadFileArgs, getToolUseLabel } from "../../../../hooks/useAiTools";
import useModal from "../../../../hooks/useModal";
import { AppRoutes } from "../../../../routes/AppRouter";
import { useSkills } from "../../../../state/api/ai/useSkills";
import { ChatHistory, ChatMessage, ChatMessageRole, Doc, Skill } from "../../../../types";
import { tryGetWorkbookContainerBlock } from "../../../../util/officeFile";
import Button from "../../buttons/Button";
import { usePage } from "../../container/Page";
import FieldWithLabel from "../../formik/FieldWithLabel";
import Icon from "../../icons/Icon";
import LoadingSpinner from "../../loader/LoadingSpinner";
import Logo from "../../logo/Logo";
import Pill from "../../pill/Pill";
import MessageTokens from "../tokens/MessageTokens";
import "./Chat.css";

interface ChatBubbleProps {
  message: ChatMessage | Doc<ChatMessage>;
  isLast: boolean;
  skill: string;
  index: number;
}

interface ToolMeta {
  count: number;
  icon: string;
  label: string;
}

function ChatFile(props: { name: string, slot?: any }) {
  return (
    <div className="d-flex align-items-center gap-3 p-1 px-2 rounded font-code text-xs bg-light border">
      <div className="d-flex flex-row gap-2">
        <Icon icon="paperclip" />
        <span>{props.name}</span>
      </div>
      {props.slot}
    </div>
  )
}

function ChatBubble(props: ChatBubbleProps) {
  const { message, skill, index } = props;

  const { skillsById } = useSkills();
  const { downloadFile } = useAiTools();

  const usedSkill = skillsById[skill];

  const mappedContent = React.useMemo(() => {
    const r: {
      text: string,
      toolUses: ToolMeta[],
      hasToolResult: boolean,
      files: string[],
      downloadables: DownloadFileArgs[]
    } = {
      text: "",
      toolUses: [],
      hasToolResult: false,
      files: [],
      downloadables: []
    }

    let toolUses: Record<string, ToolMeta> = {};

    const addToolUse = (l: string, i: string) => {
      if (toolUses[l]) {
        toolUses[l].count += 1;
        return;
      }

      toolUses[l] = {
        count: 1,
        icon: i,
        label: l
      };
    }

    let lastWasText = false;

    for (const x of message.content) {
      if (!x || typeof x.type !== "string") continue;

      const t: string = x.type;

      if (t === "text") {
        if (r.text && !lastWasText) r.text += "\n\n";
        r.text += x.text;
        lastWasText = true;
        continue;
      }

      lastWasText = false;

      if (t === "document") {
        if ("_route" in x && x._route === "container") {
          continue;
        }

        const l = x.title;
        if (l) (r as any).files.push(l);
      }
      else if (t === "tool_result") {
        r.hasToolResult = true;
      }
      else {
        if (x.name === "download_file") {
          r.downloadables.push(x.input);
        }

        const l = getToolUseLabel(t, x);

        if (l) {
          addToolUse(l.label, l.icon);
        }
      }
    }

    r.toolUses = Object.values(toolUses);

    return r;
  }, [message.content]);

  const hasContent = !!mappedContent.files.length || !!mappedContent.text;

  const role: ChatMessageRole = (
    (hasContent || !mappedContent.hasToolResult)
      ? message.role
      : "assistant"
  )

  const tb = (t: ToolMeta, y: number) => (
    <div className="d-flex flex-row align-items-center gap-2" key={`tool-use-${index}-${y}`} style={{ fontSize: "0.8em" }}>
      <div style={{ width: "20px" }} >
        <Icon icon={t.icon} size={t.icon === "check" ? 16 : 13} color="var(--primaryAccent)" />
      </div>
      {t.label && (
        <span style={{ opacity: 0.8 }}>{t.label}...</span>
      )}
      {t.count > 1 && <Pill foreground="white" background="var(--primaryAccent)">{t.count}x</Pill>}
    </div>
  )

  return (
    <>
      {
        mappedContent.toolUses?.map((x, y) => tb(x, y))
      }
      {
        (!!mappedContent.files.length || !!mappedContent.text) && (
          <div
            className={`chat-bubble d-flex flex-column gap-2 chat-bubble-${role}`}
          >
            <div className="d-flex flex-column gap-0">

              <div className="d-flex flex-column flex-sm-row w-100 justify-content-between chat-bubble-header font-code">
                <span className="chat-bubble-role">{role === "user" ? "You" : usedSkill?.label ?? "Multifamily Assistant"}</span>
                {
                  message.timestamp && (
                    <span className="chat-bubble-timestamp">
                      {
                        new Date(message.timestamp).toLocaleString("en-US", {
                          month: "2-digit",
                          day: "2-digit",
                          year: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true
                        })
                      }
                    </span>
                  )
                }
              </div>
              <MessageTokens {...props} />
            </div>
            {
              mappedContent.text && (
                <div className="chat-bubble-content d-flex flex-column" dangerouslySetInnerHTML={{ __html: marked(mappedContent.text) }} />
              )
            }
            {
              !!mappedContent.files.length && (
                <div className="d-flex flex-row flex-wrap gap-1">
                  {mappedContent.files.map((x, i) => <ChatFile name={x} key={`msg-${index}-file-${i}`} />)}
                </div>
              )
            }
            {
              !!mappedContent.downloadables.length && (
                <div className="d-flex flex-row flex-wrap gap-1">
                  {mappedContent.downloadables.map((x, i) => (
                    <ChatFile
                      name={x.filename} key={`msg-${index}-downloadable-${i}`}
                      slot={(
                        <Button
                          onClick={async () => await downloadFile(x)}
                          icon="download"
                        />
                      )}
                    />
                  ))}
                </div>
              )
            }
          </div>
        )
      }
    </>
  )
}

export default function Chat(props: ChatHistory & { skillMeta?: Skill }) {

  const { skillMeta, skill } = props;

  const {
    thinkingSince,
    chatHistory,
    isLoading,
    addMessage
  } = useAiChat(props);

  const ref = React.useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { setTitle, setSubtitle } = usePage();
  const showModal = useModal();

  React.useEffect(() => {
    if (ref.current) {
      ref.current.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  }, [chatHistory?.length, thinkingSince]);

  const startRef = React.useRef<Promise<void> | null>(null);
  const startAbortRef = React.useRef<AbortController | null>(null);

  // Reading the workbook happens before addMessage sets thinkingSince, so it
  // needs a clock of its own for the indicator to run off.
  const [preparingSince, setPreparingSince] = React.useState<number | null>(null);

  React.useEffect(() => () => startAbortRef.current?.abort(), []);

  // The caller clears this once addMessage has taken over. Clearing it here
  // would leave a frame with no indicator at all, flashing the empty state.
  const readWorkbook = async () => {
    setPreparingSince(Date.now());
    return await tryGetWorkbookContainerBlock();
  };

  React.useEffect(() => {
    if (startRef.current || !skillMeta?.firstMessage || thinkingSince || isLoading || !chatHistory || !!chatHistory.length) {
      return;
    }

    const t = new AbortController();
    startAbortRef.current = t;

    startRef.current = (async () => {
      try {
        const wb = skillMeta.modelFileUploadOnStart ? await readWorkbook() : null;

        if (t.signal.aborted) return;

        await addMessage(wb ? [wb] : undefined, t.signal);
      }
      finally {
        setPreparingSince(null);
      }
    })().finally(() => {
      startRef.current = null;
    });
  }, [isLoading, thinkingSince, skillMeta, chatHistory]);

  React.useEffect(() => {
    setTitle(AppRoutes.AI.Label);
    setSubtitle(AppRoutes.AI.Subtitle);
  }, [skillMeta, skill]);

  const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
    let binary = "";
    const bytes = new Uint8Array(buffer);
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return btoa(binary);
  };

  const fileToAnthropicSource = async (file: File): Promise<any[]> => {
    if (file.type === "application/pdf") {
      const base64Data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string).split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      return [{
        type: "document",
        source: { type: "base64", media_type: "application/pdf", data: base64Data },
        title: file.name,
        _route: "both",
      }];
    }

    const xlFileTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
      "application/msexcel",
      "application/vnd.ms-excel.sheet.macroEnabled.12",
    ];

    if (xlFileTypes.includes(file.type)) {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: "array" });

      let excelDump = "";
      workbook.SheetNames.forEach((sheetName) => {
        const worksheet = workbook.Sheets[sheetName];
        const csvData = XLSX.utils.sheet_to_csv(worksheet);
        excelDump += `\n--- Sheet: ${sheetName} ---\n${csvData}\n`;
      });

      return [
        {
          type: "document",
          source: { type: "text", media_type: "text/plain", data: excelDump.trim() },
          title: file.name,
          _route: "context",
        },
        {
          type: "document",
          source: { type: "base64", media_type: file.type, data: arrayBufferToBase64(arrayBuffer) },
          title: file.name,
          _route: "container",
        },
      ];
    }

    // Plain text: same bytes both ways.
    const contentData = await file.text();
    return [{
      type: "document",
      source: { type: "text", media_type: "text/plain", data: contentData },
      title: file.name,
      _route: "both",
    }];
  };

  const hasMessages = !!chatHistory?.length;
  const needsAttachments = !hasMessages && !!skillMeta?.fileInputRequired;

  const singleFile = !!skillMeta?.singleFileUpload;

  const fileSent = React.useMemo(() => !!chatHistory?.some(m => (
    Array.isArray(m.content) && m.content.some((b: any) => b?.type === "document" && b._route !== "container")
  )), [chatHistory]);

  const canAttach = (attached: File[]) => !singleFile || (!fileSent && !attached.length);

  return (
    <Formik
      initialValues={{
        message: "",
        files: []
      }}
      onSubmit={async (values, helpers) => {
        const contentBlocks: any[] = [];

        if (values.message.trim()) {
          contentBlocks.push({
            type: "text",
            text: values.message.trim()
          });
        }

        if (values.files.length > 0) {
          try {
            const fileBlocks = (
              await Promise.all((values.files as File[]).map(fileToAnthropicSource))
            ).flat();
            contentBlocks.push(...fileBlocks);
          } catch (err) {
            console.error("Failed to process attachments:", err);
            return;
          }
        }
        else if (needsAttachments) {
          showModal({
            text: "Please attach at least one file to use this feature.",
            type: ModalType.Error
          })
          return;
        }

        if (contentBlocks.length === 0) return;

        // A skill that wants the workbook gets it on whichever message opens
        // the chat - the silent first message, or this one.
        try {
          if (!hasMessages && skillMeta?.modelFileUploadOnStart) {
            const wb = await readWorkbook();
            if (wb) contentBlocks.push(wb);
          }

          await addMessage(contentBlocks);
        }
        finally {
          setPreparingSince(null);
        }

        helpers.resetForm();
      }}
    >
      {
        formik => (
          <Form className="position-relative d-flex flex-column w-100 pt-4 ps-sm-4 pe-sm-4 pe-3 ps-3 gap-3" style={{ flexGrow: 1, overflowY: "auto" }} >
            {
              (!chatHistory && isLoading) && (
                <div className="pt-4 ps-sm-4 pe-sm-4 pe-3 ps-3">
                  <LoadingSpinner />
                </div>
              )
            }
            {
              (preparingSince && !thinkingSince) && (
                <AiThinkingIndicator thinkingSince={preparingSince} label="Reading your workbook" />
              )
            }
            {
              chatHistory?.length
                ? chatHistory.map((m, i) => {
                  const isLast = i === chatHistory.length - 1;

                  return (
                    <React.Fragment key={`chat-bubble-${i}`}>
                      {isLast && thinkingSince && <AiThinkingIndicator thinkingSince={thinkingSince} />}
                      <ChatBubble
                        index={i}
                        isLast={isLast}

                        message={m}
                        skill={props.skill}
                      />
                    </React.Fragment>
                  )
                })
                : (
                  (!isLoading && !preparingSince) && (
                    <div className="d-flex flex-column text-center align-items-center justify-content-center p-3 gap-2 w-100">
                      <h3 className="m-0">
                        {
                          skillMeta?.label || "SILENCE IN THE VOID"
                        }
                      </h3>
                      {
                        skillMeta?.description && (
                          <span className="font-code chat-description">
                            {skillMeta.description}
                          </span>
                        )
                      }
                      {
                        (needsAttachments && !formik.values.files.length) && (
                          <Button
                            text="Attach files"
                            icon="paperclip"
                            size="large"
                            disabled={formik.isSubmitting}
                            onClick={() => inputRef?.current?.click()}
                          />
                        )
                      }
                    </div>
                  )
                )
            }
            <div ref={ref} style={{ height: "1px" }} />
            <input
              ref={inputRef}
              type="file"
              multiple={!singleFile}
              className="d-none"
              disabled={formik.isSubmitting || !canAttach(formik.values.files)}
              onChange={(e) => {
                if (e.target.files) {
                  const incoming = Array.from(e.target.files);
                  formik.setFieldValue("files", singleFile ? incoming.slice(0, 1) : [...formik.values.files, ...incoming]);
                  e.target.value = "";
                }
              }}
            />
            {
              (hasMessages || !needsAttachments || !!formik.values.files.length) && (
                <div className="w-100  mt-auto chat-field-wrapper">
                  <div className="mt-2" >
                    <div className="d-flex flex-column w-100 gap-2 pb-2 chat-field">
                      {formik.values.files.length > 0 && (
                        <FieldArray name="files">
                          {(arrayHelpers) => (
                            <div className="d-flex flex-row flex-wrap gap-2 pt-2">
                              {(formik.values.files as File[]).map((file, idx) => (
                                <ChatFile
                                  key={`file-badge-${idx}`}
                                  name={file.name}
                                  slot={(
                                    !formik.isSubmitting && (
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        icon="trash"
                                        color="var(--bs-danger)"
                                        onClick={() => arrayHelpers.remove(idx)}
                                      />
                                    )
                                  )}
                                />
                              ))}
                            </div>
                          )}
                        </FieldArray>
                      )}
                      <FieldWithLabel
                        as="textarea"
                        name="message"
                        placeholder={needsAttachments && !hasMessages ? "Attach notes" : "Talk to the MFA underwriting assistant."}
                        readOnly={formik.isSubmitting}
                      />
                      <div className="d-flex flex-row w-100 justify-content-between align-items-center">
                        <div>
                          <Button
                            text="Attach files"
                            icon="paperclip"
                            variant={needsAttachments ? "solid" : "subtle"}
                            disabled={formik.isSubmitting || !canAttach(formik.values.files)}
                            onClick={() => inputRef?.current?.click()}
                          />
                        </div>
                        <Button
                          type="submit"
                          disabled={!formik.values.message && (needsAttachments && !formik.values.files.length)}
                          onClick={() => formik.submitForm()}
                          text="Send message"
                          icon="send"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )
            }
          </Form>
        )
      }
    </Formik >
  )
}

function AiLoader() {
  return (
    <div className="d-flex">
      <div className="ai-thinking-indicator">
        <Logo size={40} />
      </div>
    </div>
  )
}

function AiThinkingIndicator(props: { thinkingSince: number, label?: string }) {

  const { thinkingSince } = props;

  const [elapsed, setElapsed] = React.useState(0);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - thinkingSince) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [thinkingSince]);

  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;

  const formatted = mins === 0 ? `${secs}s` : `${mins}:${secs.toString().padStart(2, "0")}m`;

  return (
    <div className="position-relative">
      <div className="d-flex flex-row align-items-center gap-2">
        <AiLoader />
        <span>{props.label ?? "Assistant is working"}...{formatted}</span>
      </div>
    </div>
  )
}